export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  firstItemOnPage: number;
  lastItemOnPage: number;
}

export type TenantStatus =
  | 'PendingSubscription'
  | 'Active'
  | 'Suspended'
  | 'Trial'
  | 'TrialGracePeriod'
  | 'PastDue'
  | 'Expired';

export interface AdminTenantListDto {
  id: string;
  name: string;
  slug: string;
  avatarUrl: string | null;
  ownerId: string;
  ownerName: string | null;
  status: TenantStatus;
  countryCode: string | null;
  isActive: boolean;
  maxUsers: number;
  trialEndsAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface TenantDto {
  id: string;
  name: string;
  slug: string;
  avatarUrl: string | null;
  ownerId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export const PlanOrigin = {
  /** La cuenta lo contrato por si misma. Se cancela por los caminos normales. */
  SelfService: 'SelfService',
  /** Un administrador se lo otorgo. Solo un administrador se lo quita. */
  AdminAssigned: 'AdminAssigned',
} as const;

export type PlanOrigin = (typeof PlanOrigin)[keyof typeof PlanOrigin];

export interface AdminTenantDetailDto {
  id: string;
  name: string;
  slug: string;
  avatarUrl: string | null;
  ownerId: string;
  ownerName: string | null;
  status: TenantStatus;
  countryCode: string | null;
  isActive: boolean;
  maxUsers: number;
  memberCount: number;
  tierName: string;
  tierMaxActors: number;
  tierMaxSources: number;
  tierMaxTopics: number;
  tierMaxAlerts: number;
  tierMaxScheduledReports: number | null;
  trialEndsAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  planOrigin: PlanOrigin;
  assignedByAdminId: string | null;
  assignedAt: string | null;
  tierMaxRegions: number;
  /** Solo lo trae el detalle; el listado no cuenta regiones por fila. */
  regionCount: number;
}

export interface AdminTenantMemberDto {
  membershipId: string;
  userId: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  role: string;
  isActive: boolean;
  joinedAt: string;
  leftAt: string | null;
}

export interface TenantMemberPaginationParams {
  page?: number;
  pageSize?: number;
}

export interface MembershipDto {
  id: string;
  userId: string;
  tenantId: string;
  role: string;
  isActive: boolean;
  joinedAt: string;
  leftAt: string | null;
}

export interface TenantPaginationParams {
  page?: number;
  pageSize?: number;
  status?: TenantStatus | null;
  isActive?: boolean | null;
  countryId?: string | null;
  planId?: string | null;
  searchTerm?: string | null;
  createdFrom?: string | null;
  createdTo?: string | null;
}

export interface ToggleTenantStatusRequest {
  reason: string;
}

export type BillingCycle = 'Monthly' | 'Annual';

export interface AssignPlanRequest {
  planId: string;
  billingCycle: BillingCycle;
  /** Asignar aunque la cuenta ya use más de lo que el plan permite. */
  force?: boolean;
}

/** Un recurso que la cuenta ya usa por encima del tope del plan que se le quiere asignar. */
export interface ExceededResource {
  /** Nombre del backend: Users, Regions, ActiveTopics, ActiveActors, ActiveAlerts. */
  resource: string;
  current: number;
  limit: number;
}

export interface RevertPlanAssignmentRequest {
  reason: string | null;
}

export interface AdminMemberAuthProvider {
  provider: string;
  connectedAt: string;
  lastUsedAt: string | null;
}

export interface AdminMemberWorkspace {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  role: string;
  isActive: boolean;
  joinedAt: string;
  leftAt: string | null;
}

export interface AdminMemberActivity {
  tenantId: string;
  action: string;
  oldValue: string | null;
  newValue: string | null;
  occurredAt: string;
}

export interface AdminUserDetailDto {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  verificationStatus: string;
  isLocked: boolean;
  lockedUntil: string | null;
  failedLoginAttempts: number;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  authProviders: AdminMemberAuthProvider[];
  workspaces: AdminMemberWorkspace[];
  recentActivity: AdminMemberActivity[];
}
